import React from "react";
import { useShallow } from "zustand/react/shallow";
import {
    RoomPlayer,
    RoomPlayerState,
    RoomState,
    RoomPlayerAction as RoomPlayerActionEnum
} from "@uno/protocol";
import useStoreRoom from "store/useStoreRoom";
import CircleProgress from "./CircleProgress";
import SkipTurn from "./Action/SkipTurn";
import BackInGame from "./Action/BackInGame";
import Draw from "./Action/Draw";
import EmotePicker from "./Action/EmotePicker";
import CardsContainer from "component/Card/CardsContainer";
import Avatar from "component/Ui/Avatar";
import RoomPlayerAction from "../RoomPlayerAction";
import RoomPlayerEmote from "../RoomPlayerEmote";
import { useRoomLayout } from "context/RoomLayoutContext";

type PlayerProps = {
    roomPlayer: RoomPlayer,
}

const Player = ({ roomPlayer }: PlayerProps) => {
    const room = useStoreRoom(useShallow(state => ({ id: state.room?.id, state: state.room?.state, accessibility: state.room?.accessibility })));
    const roomIsPlaying = React.useMemo(() => room?.state === RoomState.Playing, [room?.state]);
    const isMyTurn = React.useMemo(() => roomIsPlaying && [RoomPlayerState.Active, RoomPlayerState.AwaitingAction].includes(roomPlayer.state as RoomPlayerState), [roomIsPlaying, roomPlayer?.state, roomPlayer?.action]);
    const isAbandoned = roomPlayer.state === RoomPlayerState.Abandoned;
    const isChoosing = [RoomPlayerActionEnum.ChoosingColor, RoomPlayerActionEnum.ChoosingSwapTarget].includes(roomPlayer.action);
    const { activeHandBottom, inactiveHandBottom } = useRoomLayout();

    return (
        <div data-player-id={roomPlayer?.player.id}>
            <div id="player" className={`${isMyTurn ? "border-none active-halo" : "border-2 border-white/30"} ${isAbandoned ? "opacity-40" : ""}`}>
                <RoomPlayerAction roomPlayer={roomPlayer} />
                <RoomPlayerEmote playerId={roomPlayer.player.id} />

                <Avatar src={roomPlayer.player.avatar} className={`w-full h-full absolute top-0 left-0 z-0 ${isMyTurn ? "brightness-90" : "brightness-50"}`} />
                {isMyTurn && <CircleProgress turnEndsAt={roomPlayer.turnEndsAt} turnDuration={roomPlayer.turnDuration} />}

                {roomPlayer.state === RoomPlayerState.AwaitingAction && !isChoosing && <SkipTurn />}
                {roomPlayer.state === RoomPlayerState.Inactive && <BackInGame />}
                {![RoomPlayerState.Inactive, RoomPlayerState.AwaitingAction, RoomPlayerState.Abandoned].includes(roomPlayer.state) && !isChoosing && roomIsPlaying && isMyTurn && <Draw />}

                {roomIsPlaying && <EmotePicker />}
            </div>

            <div
                className="cards-container fixed z-[25] left-1/2 -translate-x-1/2 transition-[bottom,opacity] duration-300 ease-out"
                style={{ bottom: `${isMyTurn ? activeHandBottom : inactiveHandBottom}px` }}
            >
                <CardsContainer className={`${!isMyTurn && roomIsPlaying ? "hand-dimmed" : ""} ${isAbandoned ? "opacity-40" : ""}`} playerId={roomPlayer.player.id} isHoverable={isMyTurn && roomIsPlaying && !isAbandoned} />
            </div>
        </div>
    )
}

export default React.memo(Player);
